import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import PerformanceMetricsCard from './components/PerformanceMetricsCard';
import PLChart from './components/PLChart';
import AssetAllocationChart from './components/AssetAllocationChart';
import StrategyPerformanceTable from './components/StrategyPerformanceTable';
import RiskMetricsPanel from './components/RiskMetricsPanel';
import PerformanceFilters from './components/PerformanceFilters';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { astraAPI } from '../../services/astraApi';
import { SkeletonCard, SkeletonList } from '../../components/ui/Skeleton';
import useAutoRefresh from '../../hooks/useAutoRefresh';

/**
 * Portfolio Performance Analytics
 *
 * 🔧 PRODUCTION VERSION (2025-12-28): Enhanced with 2025 UI Best Practices
 *
 * Enhancements:
 * - Framer Motion animations for smooth transitions
 * - useAutoRefresh hook for butter-smooth data refresh
 * - Skeleton loading states
 * - Glass-card hover effects
 * - Real Portfolio Performance API integration
 */

const PortfolioPerformanceAnalytics = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedView, setSelectedView] = useState('overview');
  const [filters, setFilters] = useState({});
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Real data states
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Auto-refresh using custom hook (every 30 seconds)
  const { refreshCount } = useAutoRefresh({
    intervalMs: 30000,
    enabled: true,
    onRefresh: () => fetchPerformanceData(true)
  });

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  const fetchPerformanceData = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch real performance data from API
      const performance = await astraAPI.getPortfolioPerformance();
      const portfolio = await astraAPI.getPortfolioSummary();

      console.log('📊 Performance Data Loaded:', {
        total_value: performance.total_value,
        total_pnl: performance.total_pnl,
        win_rate: performance.win_rate
      });

      setPerformanceData({
        ...performance,
        portfolio_summary: portfolio
      });
      setLastUpdated(new Date());

    } catch (err) {
      console.error('Failed to fetch performance data:', err);
      setError(`Failed to load performance data: ${err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Build metrics cards from REAL data
  const buildPerformanceMetrics = () => {
    if (!performanceData) return [];

    const formatCurrency = (value) => {
      if (value >= 10000000) {
        return `₹${(value / 10000000).toFixed(2)}Cr`;
      } else if (value >= 100000) {
        return `₹${(value / 100000).toFixed(2)}L`;
      } else {
        return `₹${value.toFixed(0)}`;
      }
    };

    return [
      {
        title: 'Total Portfolio Value',
        value: formatCurrency(performanceData.total_value || 0),
        change: `${performanceData.pnl_percentage >= 0 ? '+' : ''}${performanceData.pnl_percentage?.toFixed(2) || 0}%`,
        changeType: performanceData.pnl_percentage >= 0 ? 'positive' : 'negative',
        icon: 'Wallet',
        trend: true
      },
      {
        title: 'Total P&L (All Time)',
        value: formatCurrency(performanceData.total_pnl || 0),
        change: `${performanceData.pnl_percentage >= 0 ? '+' : ''}${performanceData.pnl_percentage?.toFixed(2) || 0}%`,
        changeType: performanceData.total_pnl >= 0 ? 'positive' : 'negative',
        icon: 'TrendingUp',
        trend: true
      },
      {
        title: 'Realized P&L',
        value: formatCurrency(performanceData.realized_pnl || 0),
        change: `${performanceData.total_trades || 0} trades`,
        changeType: performanceData.realized_pnl >= 0 ? 'positive' : 'negative',
        icon: 'CheckCircle',
        trend: true
      },
      {
        title: 'Unrealized P&L',
        value: formatCurrency(performanceData.unrealized_pnl || 0),
        change: 'Live positions',
        changeType: performanceData.unrealized_pnl >= 0 ? 'positive' : 'negative',
        icon: 'Clock',
        trend: true
      },
      {
        title: 'Sharpe Ratio',
        value: performanceData.sharpe_ratio?.toFixed(2) || '0.00',
        change: performanceData.sharpe_ratio > 1.0 ? 'Good' : performanceData.sharpe_ratio > 0 ? 'Fair' : 'Poor',
        changeType: performanceData.sharpe_ratio > 1.0 ? 'positive' : performanceData.sharpe_ratio > 0 ? 'neutral' : 'negative',
        icon: 'Target',
        trend: false
      },
      {
        title: 'Max Drawdown',
        value: `-${performanceData.max_drawdown?.toFixed(2) || 0}%`,
        change: performanceData.max_drawdown < 10 ? 'Low risk' : performanceData.max_drawdown < 20 ? 'Medium' : 'High',
        changeType: performanceData.max_drawdown < 10 ? 'positive' : 'neutral',
        icon: 'TrendingDown',
        trend: false
      },
      {
        title: 'Win Rate',
        value: `${performanceData.win_rate?.toFixed(1) || 0}%`,
        change: `${performanceData.winning_trades || 0}/${performanceData.total_trades || 0}`,
        changeType: performanceData.win_rate >= 60 ? 'positive' : performanceData.win_rate >= 50 ? 'neutral' : 'negative',
        icon: 'Award',
        trend: false
      },
      {
        title: 'Total Trades',
        value: performanceData.total_trades?.toString() || '0',
        change: `Win: ${performanceData.winning_trades || 0} | Loss: ${performanceData.losing_trades || 0}`,
        changeType: 'neutral',
        icon: 'Layers',
        trend: false
      }
    ];
  };

  const viewOptions = [
    { key: 'overview', label: 'Overview', icon: 'BarChart3' },
    { key: 'performance', label: 'Performance', icon: 'TrendingUp' },
    { key: 'risk', label: 'Risk Analysis', icon: 'Shield' },
    { key: 'allocation', label: 'Allocation', icon: 'PieChart' }
  ];

  const handleRefresh = () => {
    fetchPerformanceData(true);
  };

  const handleExport = (format) => {
    console.log(`Exporting portfolio data as ${format}...`);
    // TODO: Implement export functionality
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters updated:', newFilters);
  };

  const renderContent = () => {
    const metrics = buildPerformanceMetrics();

    switch (selectedView) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Performance Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {metrics?.map((metric, index) => (
                <PerformanceMetricsCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  change={metric?.change}
                  changeType={metric?.changeType}
                  icon={metric?.icon}
                  trend={metric?.trend}
                />
              ))}
            </div>
            {/* Charts Row */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <PLChart />
              <AssetAllocationChart />
            </div>
            {/* Strategy Performance Table */}
            <StrategyPerformanceTable />
          </div>
        );

      case 'performance':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {metrics?.slice(0, 4)?.map((metric, index) => (
                <PerformanceMetricsCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  change={metric?.change}
                  changeType={metric?.changeType}
                  icon={metric?.icon}
                  trend={metric?.trend}
                />
              ))}
            </div>
            <PLChart />
            <StrategyPerformanceTable />
          </div>
        );

      case 'risk':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {metrics?.slice(4, 8)?.map((metric, index) => (
                <PerformanceMetricsCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  change={metric?.change}
                  changeType={metric?.changeType}
                  icon={metric?.icon}
                  trend={metric?.trend}
                />
              ))}
            </div>
            <RiskMetricsPanel />
          </div>
        );

      case 'allocation':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[metrics?.[0], metrics?.[1], metrics?.[6], metrics?.[7]]?.map((metric, index) => (
                <PerformanceMetricsCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  change={metric?.change}
                  changeType={metric?.changeType}
                  icon={metric?.icon}
                  trend={metric?.trend}
                />
              ))}
            </div>
            <AssetAllocationChart />
            <StrategyPerformanceTable />
          </div>
        );

      default:
        return null;
    }
  };

  // Loading state with skeleton
  if (loading && !performanceData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        <main className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } pt-16`}>
          <div className="p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
              <h1 className="text-2xl font-bold text-foreground">Portfolio Performance Analytics</h1>
              <p className="text-muted-foreground mt-1">Loading performance metrics...</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <SkeletonCard className="h-28" />
              <SkeletonCard className="h-28" />
              <SkeletonCard className="h-28" />
              <SkeletonCard className="h-28" />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
              <SkeletonCard className="h-72" />
              <SkeletonCard className="h-72" />
            </div>
            <SkeletonCard className="h-64" />
          </div>
        </main>
      </div>
    );
  }

  // Error state with retry
  if (error && !performanceData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        <main className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } pt-16`}>
          <div className="p-6 flex items-center justify-center h-screen">
            <div className="text-center">
              <Icon name="AlertTriangle" size={48} className="text-error mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Failed to Load Performance Data</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => fetchPerformanceData()} iconName="RefreshCw">
                Retry
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main className={`transition-all duration-300 ${
        isSidebarCollapsed ? 'ml-16' : 'ml-64'
      } pt-16`}>
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-center justify-between"
          >
            <div>
              <Breadcrumb />
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="PieChart" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Portfolio Performance Analytics</h1>
                  <p className="text-muted-foreground">
                    Comprehensive P&L analysis and risk assessment • Refresh #{refreshCount}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                iconName="RefreshCw"
                iconSize={16}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('pdf')}
                iconName="Download"
                iconSize={16}
              >
                Export Report
              </Button>
            </div>
          </motion.div>

          {/* View Selector */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="flex items-center justify-between"
          >
            <div className="flex bg-muted rounded-lg p-1">
              {viewOptions?.map((view) => (
                <button
                  key={view?.key}
                  onClick={() => setSelectedView(view?.key)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                    selectedView === view?.key
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={view?.icon} size={16} />
                  <span>{view?.label}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <div className={`w-2 h-2 rounded-full ${refreshing ? 'bg-warning animate-pulse' : 'bg-success animate-live-pulse'}`}></div>
              <span>{refreshing ? 'Updating...' : `Live • ${lastUpdated?.toLocaleTimeString('en-IN')}`}</span>
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
          >
            <PerformanceFilters onFiltersChange={handleFiltersChange} />
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <QuickActionToolbar
              onRefresh={handleRefresh}
              onExport={handleExport}
              onFilter={handleFiltersChange}
              className="mb-6"
            />
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
          >
            {renderContent()}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default PortfolioPerformanceAnalytics;
